/* eslint-disable react/no-array-index-key */
"use client";

import { PinAltIcon } from "@/components/icons-lib";
import { cn } from "@/shared/helpers/cn.helper";
import { highlightMatchSegments } from "@/shared/utils/highlight-match-segments.util";

import { splitSuggestionLines } from "./address-suggestion-list.utils";

export interface AddressSuggestionListItem {
  latitude: number;
  longitude: number;
  formattedAddress?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface AddressSuggestionListProps {
  items: AddressSuggestionListItem[];
  query: string;
  highlightIndex: number;
  onHighlightChange: (index: number) => void;
  onSelect: (item: AddressSuggestionListItem, index: number) => void;
  className?: string;
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const segments = highlightMatchSegments(text, query);
  return (
    <>
      {segments.map((segment, index) =>
        segment.match ? (
          <span
            key={`${segment.text}-${index}`}
            className="font-semibold"
          >
            {segment.text}
          </span>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        ),
      )}
    </>
  );
}

export function AddressSuggestionList({
  items,
  query,
  highlightIndex,
  onHighlightChange,
  onSelect,
  className,
}: AddressSuggestionListProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ul
      className={cn("max-h-60 overflow-y-auto py-1", className)}
      role="listbox"
    >
      {items.map((item, index) => {
        const { primary, secondary } = splitSuggestionLines(item);
        const fallback = `${item.latitude.toFixed(4)}, ${item.longitude.toFixed(4)}`;
        const primaryText = primary || fallback;
        const isHighlighted = index === highlightIndex;

        return (
          <li
            key={`${item.latitude}-${item.longitude}-${item.formattedAddress ?? ""}-${index}`}
            aria-selected={isHighlighted}
            role="option"
          >
            <button
              className={cn(
                "flex w-full items-start gap-2 px-3 py-2 text-left outline-none",
                isHighlighted ? "bg-primary-soft" : "hover:bg-primary-soft",
              )}
              type="button"
              onClick={() => onSelect(item, index)}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => onHighlightChange(index)}
            >
              <PinAltIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-gray-900">
                  <HighlightedText
                    query={query}
                    text={primaryText}
                  />
                </span>
                {secondary ? (
                  <span className="block truncate text-xs text-gray-500">
                    <HighlightedText
                      query={query}
                      text={secondary}
                    />
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
